import React from 'react';
import { REEBELO_TZ_NAME } from '@lambda/reebelo';
import { OrderInvoiceType } from '@lambda/apis/src/order/types';
import { InvoiceRequestByOrderVendor } from '@lambda/business/orders/invoiceRequestKey';
import useOrderInvoices from '@/components/accounts/Orders/my-orders/use-order-invoices';
import { handleFilterReebeloCarePackages } from '@/lib/smartForm/orderUnitUtil';
import { formatDayJsDateShort } from '@/components/track-your-order/utils';
import settings, { priceFormater } from '@/settings';
import ShipmentDetail, { shipmentViewLineItems } from './ShipmentDetail';
import {
  ShipmentLineItem,
  Shipment,
  CustomerOrder,
} from '@/lib/smartForm/types';
import PairItWith from '../v2/product/sections/secondary/PairItWith';
import i18n from '@/settings/i18n';

const t = i18n.init('orders');

const OrderMeta = ({
  head,
  detail,
  widthClass,
  headSize = 'text-base',
}: {
  head: string;
  detail: string;
  headSize?: string;
  widthClass: string;
}) => (
  <div
    className={`flex ${widthClass} flex-row flex-wrap content-start items-start text-sm`}
  >
    <h2 className={`w-full ${headSize} font-bold leading-tight`}>{head}</h2>
    <span className="w-full text-sm leading-tight">{detail}</span>
  </div>
);

export default function OrderUnit(props: {
  order: CustomerOrder;
  index: number;
  invoiceRequests?: InvoiceRequestByOrderVendor;
}) {
  const { invoices: orderInvoices } = useOrderInvoices(props.order.id);
  // One per order, covering every seller's shipment (RT-17241).
  const proofOfPurchase = orderInvoices?.find(
    (invoice) => invoice.type === OrderInvoiceType.PROOF_OF_PURCHASE,
  );

  const { order, index, invoiceRequests } = props;
  const { orderName, processedAt, shipments, totalPrice, lineItems } = order;

  // Keep eligible Products
  const filteredLineItems = lineItems?.filter(
    (item) => item?.category && item?.psku,
  );

  // Extract a list of RebeeloCare title
  const reebeloCares = handleFilterReebeloCarePackages(shipments);
  /*
   * Below are normalized data for UI display
   */
  const { store } = settings;
  // It replaces each shipment's own proof of purchase.
  const hideShipmentProofOfPurchase = Boolean(proofOfPurchase?.url);
  // Shown once, in the first shipment view that lists a row, where that
  // shipment's own one used to be.
  let pendingProofOfPurchaseUrl = proofOfPurchase?.url;

  const takeProofOfPurchaseUrl = (
    shipment: Shipment,
    isRefundedView: boolean,
  ) => {
    if (shipmentViewLineItems(shipment, isRefundedView).length === 0) {
      return undefined;
    }
    const url = pendingProofOfPurchaseUrl;

    pendingProofOfPurchaseUrl = undefined;

    return url;
  };
  const storeTimeZone = REEBELO_TZ_NAME[store as keyof typeof REEBELO_TZ_NAME];
  const normalizedProcessData =
    processedAt !== null
      ? t`Placed ${{
          date: formatDayJsDateShort(processedAt, storeTimeZone),
        }}`
      : '';
  const normalizedOrderNumber = t`Order ${{ orderName }}`;
  const normalizedTotalPrice =
    totalPrice !== null ? priceFormater.short(Number(totalPrice?.amount)) : '';
  const {
    firstName,
    lastName,
    address1,
    city,
    country,
    province,
    zip,
    address2,
  } = order.shippingAddress;
  const normalizedShipTo = `${firstName} ${lastName}, ${address1}${
    address2 ? `, ${address2}` : ''
  }, ${city} ${province} ${zip}, ${country}`;

  return (
    <>
      <div className="mb-8 flex w-full flex-row flex-wrap">
        <div className="flow-root w-full rounded border border-gray-200 bg-gray-300 px-4 py-3.5 sm:flex">
          <div className="flex w-full pt-1 xs:pt-3 sm:w-1/2 sm:pt-0">
            <OrderMeta
              headSize="text-lg"
              widthClass="w-3/5 sm:gap-0 gap-1"
              head={normalizedOrderNumber}
              detail={normalizedProcessData}
            />
            <OrderMeta
              head={t`Total`}
              detail={normalizedTotalPrice}
              widthClass="w-2/5 sm:gap-0 gap-1 text-right sm:text-justify"
            />
          </div>
          <div className="flex w-1/2 py-4 !pb-1 xs:py-8 xs:!pb-2 sm:py-0">
            <OrderMeta
              head={t`Ship To`}
              detail={normalizedShipTo}
              widthClass="w-4/5 sm:gap-0 gap-1 hidden sm:block"
            />
            <OrderMeta
              head={t`Shipping to:`}
              detail={normalizedShipTo}
              widthClass="w-4/5 sm:gap-0 gap-1 block sm:hidden"
            />
          </div>
        </div>
        {shipments
          .filter((shipment: Shipment) =>
            shipment.lineItems.some(
              (lineItem: ShipmentLineItem) =>
                !(
                  lineItem.name.toLowerCase().includes('reebelocare') ||
                  lineItem.name.toLowerCase().includes('express shipping')
                ),
            ),
          )
          .flatMap((shipment: Shipment, idx: number) => {
            const hasRefundedItems = shipment.lineItems.some(
              (lineItem: ShipmentLineItem) => lineItem.isRefunded,
            );
            const hasPartialRefund =
              shipment.lineItems.reduce(
                (sum, i) => sum + (i.refundQuantity ?? 0),
                0,
              ) > 0 &&
              shipment.lineItems.reduce(
                (sum, i) => sum + (i.refundQuantity ?? 0),
                0,
              ) < shipment.lineItems.reduce((sum, i) => sum + i.quantity, 0);

            const result = [];

            if (hasRefundedItems) {
              result.push(
                <ShipmentDetail
                  key={`${idx}-refunded`}
                  order={order}
                  shipment={shipment}
                  shipmentIndex={idx + 1}
                  processedAt={processedAt}
                  reebeloCares={reebeloCares}
                  storeTimeZone={storeTimeZone}
                  lineItems={lineItems ?? []}
                  isRefundedView={true}
                  orderInvoices={orderInvoices}
                  invoiceRequests={invoiceRequests}
                  hideShipmentProofOfPurchase={hideShipmentProofOfPurchase}
                  proofOfPurchaseUrl={takeProofOfPurchaseUrl(shipment, true)}
                />,
              );
            }

            if (!hasRefundedItems || hasPartialRefund) {
              result.push(
                <ShipmentDetail
                  key={`${idx}-active`}
                  order={order}
                  shipment={shipment}
                  shipmentIndex={idx + 1}
                  processedAt={processedAt}
                  reebeloCares={reebeloCares}
                  storeTimeZone={storeTimeZone}
                  lineItems={lineItems ?? []}
                  orderInvoices={orderInvoices}
                  invoiceRequests={invoiceRequests}
                  hideShipmentProofOfPurchase={hideShipmentProofOfPurchase}
                  proofOfPurchaseUrl={takeProofOfPurchaseUrl(shipment, false)}
                />,
              );
            }

            return result;
          })}
      </div>
      {index === 0 && filteredLineItems && filteredLineItems?.length > 0 && (
        <PairItWith isAccount psku={filteredLineItems?.[0]?.psku ?? ''} />
      )}
    </>
  );
}
